// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import { ErrorEvent, EventHint, SamplingContext } from "@sentry/core";
import * as Sentry from "@sentry/nextjs";

import { SENTRY_DETAILS } from "./sentry.config";

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;

// * Sync between sentry.{client|server}.config.ts
const whitelists: {
  requestUrl: RegExp;
  type: RegExp;
  value: RegExp;
}[] = [
  {
    requestUrl: new RegExp("^/login$"),
    type: new RegExp("AxiosError"),
    value: new RegExp("Request failed with status code 400"),
  },
  {
    requestUrl: new RegExp(".*"),
    type: new RegExp("Error"),
    value: new RegExp("Abort fetching component for route"),
  },
  {
    requestUrl: new RegExp("/_next/static/chunks/pages/.*\\.js"),
    type: new RegExp("Error"),
    value: new RegExp("Failed to load script"),
  },
];
const tracesSampler = ({
  name,
  parentSampled,
}: SamplingContext): boolean | number => {
  // These are important - take a big sample
  if (name.includes("auth")) return 1;
  if (name.includes("kyc")) return 1;
  if (name.includes("verify")) return 1;

  // These are less important or happen much more frequently - only take 1%
  if (name.includes("healthcheck")) return 0.01;
  if (name.includes("comment")) return 0.01;

  // Continue trace decision, if there is any parentSampled information
  if (typeof parentSampled === "boolean") return parentSampled;

  // Else, use default sample rate
  return 0.5;
};
const beforeSend = (
  event: ErrorEvent,
  hint: EventHint
): PromiseLike<ErrorEvent | null> | ErrorEvent | null => {
  // Ignore "Timeout" thrown by Google reCaptcha as it doesn't impact end user https://github.com/getsentry/sentry-javascript/issues/2514
  if (hint.originalException === "Timeout") return null;

  // https://fundnel.atlassian.net/browse/AG-4999
  if (event.message?.includes("[next-auth][error][CLIENT_FETCH_ERROR]")) {
    return null;
  }

  const requestUrl = event.request?.url;
  const type = event.exception?.values?.[0]?.type;
  const value = event.exception?.values?.[0]?.value;

  // If either of these are missing, return the event as is
  if (!requestUrl || !type || !value) return event;

  // If the event matches the whitelist, return null to drop it
  if (
    whitelists.some(
      (w) =>
        w.requestUrl.test(requestUrl) &&
        w.type.test(type) &&
        w.value.test(value)
    )
  ) {
    console.info(`[Sentry] Filtering out event: ${type} - ${value}`);
    return null;
  }

  // Otherwise, return the event as is
  return event;
};
// * END Sync between sentry.{client|edge|server}.config.ts

/** Pack integrations.sentry → TF sets SENTRY_ENABLED. Default off when unset/false. */
function isSentryEnabled(): boolean {
  const raw =
    process.env.NEXT_PUBLIC_SENTRY_ENABLED ?? process.env.SENTRY_ENABLED;
  if (raw === undefined || raw === "") {
    // Legacy Alta deploys before pack wiring: enable only if DSN is present
    return Boolean(SENTRY_DETAILS.SENTRY_DSN);
  }
  return ["1", "true", "yes"].includes(String(raw).toLowerCase());
}

if (isSentryEnabled() && SENTRY_DETAILS.SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DETAILS.SENTRY_DSN,

    tracesSampler,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    replaysOnErrorSampleRate: 1.0,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,

    // You can remove this option if you're not planning to use the Sentry Session Replay feature:
    integrations: [
      Sentry.replayIntegration({
        // Additional Replay configuration goes in here, for example:
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
    ],
    beforeSend,
  });
} else {
  console.info(
    "[monitoring] Sentry disabled (integrations.sentry=false or no DSN)"
  );
}
